import { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Save, X, Tag, TrendingUp } from 'lucide-react';
import type { Coupon } from '../../App';
import { couponAPI } from '../../lib/api';

interface CouponManagementProps {
  restaurantId: string;
}

export function CouponManagement({ restaurantId }: CouponManagementProps) {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<Coupon>>({
    discountType: 'percentage',
    isActive: true
  });

  const closeFormModal = () => {
    setIsAdding(false);
    setEditingId(null);
    setForm({ discountType: 'percentage', isActive: true });
  };

  const isFormModalOpen = Boolean(isAdding || editingId);

  const mapApiCoupon = (coupon: any): Coupon => ({
    id: String(coupon.id),
    restaurantId: String(coupon.restaurant_id ?? coupon.restaurantId ?? restaurantId),
    code: String(coupon.code ?? ''),
    description: String(coupon.description ?? ''),
    discountType: (coupon.discount_type ?? coupon.discountType) === 'flat' ? 'flat' : 'percentage',
    discountValue: Number(coupon.discount_value ?? coupon.discountValue ?? 0),
    minOrderValue: Number(coupon.min_order_value ?? coupon.minOrderValue ?? 0),
    maxDiscount:
      coupon.max_discount !== null && coupon.max_discount !== undefined
        ? Number(coupon.max_discount)
        : coupon.maxDiscount !== null && coupon.maxDiscount !== undefined
          ? Number(coupon.maxDiscount)
          : undefined,
    isActive: Boolean(coupon.is_active ?? coupon.isActive),
    validFrom: String(coupon.valid_from ?? coupon.validFrom ?? new Date().toISOString()),
    validUntil: String(coupon.valid_until ?? coupon.validUntil ?? new Date().toISOString()),
    usageLimit:
      coupon.usage_limit !== null && coupon.usage_limit !== undefined
        ? Number(coupon.usage_limit)
        : coupon.usageLimit !== null && coupon.usageLimit !== undefined
          ? Number(coupon.usageLimit)
          : undefined,
    usedCount: Number(coupon.used_count ?? coupon.usedCount ?? 0),
  });

  const loadCoupons = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await couponAPI.getByRestaurant(restaurantId, true);
      setCoupons((response.data || []).map(mapApiCoupon));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load coupons');
      setCoupons([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCoupons();
  }, [restaurantId]);

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  useEffect(() => {
    if (!isFormModalOpen) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeFormModal();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormModalOpen]);

  const handleAdd = () => {
    if (!form.code || !form.discountValue || !form.minOrderValue) return;

    couponAPI
      .create({
        restaurantId,
        code: form.code.toUpperCase(),
        description: form.description || '',
        discountType: form.discountType || 'percentage',
        discountValue: form.discountValue,
        minOrderValue: form.minOrderValue,
        maxDiscount: form.maxDiscount,
        validFrom: form.validFrom || new Date().toISOString(),
        validUntil: form.validUntil || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        usageLimit: form.usageLimit,
      })
      .then(async () => {
        await loadCoupons();
        setForm({ discountType: 'percentage', isActive: true });
        setIsAdding(false);
        setError(null);
        setSuccessMessage('Coupon created successfully');
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to create coupon');
      });
  };

  const handleUpdate = () => {
    if (!editingId || !form.code || !form.discountValue || !form.minOrderValue) return;

    couponAPI
      .update(editingId, {
        description: form.description || '',
        discountValue: form.discountValue,
        minOrderValue: form.minOrderValue,
        maxDiscount: form.maxDiscount,
        isActive: form.isActive !== false,
        validFrom: form.validFrom,
        validUntil: form.validUntil,
        usageLimit: form.usageLimit,
      })
      .then(async () => {
        await loadCoupons();
        setEditingId(null);
        setForm({ discountType: 'percentage', isActive: true });
        setError(null);
        setSuccessMessage('Coupon updated successfully');
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to update coupon');
      });
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this coupon?')) {
      couponAPI
        .delete(id)
        .then(async () => {
          await loadCoupons();
          setError(null);
          setSuccessMessage('Coupon deleted successfully');
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : 'Failed to delete coupon');
        });
    }
  };

  const toggleStatus = (id: string) => {
    const targetCoupon = coupons.find(coupon => coupon.id === id);
    if (!targetCoupon) return;

    couponAPI
      .update(id, { isActive: !targetCoupon.isActive })
      .then(loadCoupons)
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to update coupon status');
      });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-gray-900">Coupon Management</h1>
          <p className="text-gray-600 text-sm">Create and manage discount coupons for your restaurant</p>
        </div>
        <button
          onClick={() => {
            setEditingId(null);
            setForm({ discountType: 'percentage', isActive: true });
            setIsAdding(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create Coupon
        </button>
      </div>

      {/* Add/Edit Form */}
      {isFormModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          onClick={closeFormModal}
        >
          <div
            className="bg-white rounded-xl shadow-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start justify-between gap-4">
              <h3 className="text-gray-900">{isAdding ? 'Create New Coupon' : 'Edit Coupon'}</h3>
              <button
                onClick={closeFormModal}
                className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 mb-2">Coupon Code</label>
              <input
                type="text"
                value={form.code || ''}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
                placeholder="e.g., WELCOME10"
                maxLength={20}
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Discount Type</label>
              <select
                value={form.discountType}
                onChange={(e) => setForm({ ...form, discountType: e.target.value as 'percentage' | 'flat' })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="percentage">Percentage (%)</option>
                <option value="flat">Flat Amount (₹)</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 mb-2">
                {form.discountType === 'percentage' ? 'Discount Percentage' : 'Discount Amount (₹)'}
              </label>
              <input
                type="number"
                value={form.discountValue || ''}
                onChange={(e) => setForm({ ...form, discountValue: parseFloat(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={form.discountType === 'percentage' ? '10' : '50'}
                min="0"
                max={form.discountType === 'percentage' ? '100' : undefined}
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Minimum Order Value (₹)</label>
              <input
                type="number"
                value={form.minOrderValue || ''}
                onChange={(e) => setForm({ ...form, minOrderValue: parseFloat(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="300"
                min="0"
              />
            </div>
            {form.discountType === 'percentage' && (
              <div>
                <label className="block text-gray-700 mb-2">Maximum Discount (₹) - Optional</label>
                <input
                  type="number"
                  value={form.maxDiscount || ''}
                  onChange={(e) => setForm({ ...form, maxDiscount: e.target.value ? parseFloat(e.target.value) : undefined })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="100"
                  min="0"
                />
              </div>
            )}
            <div>
              <label className="block text-gray-700 mb-2">Usage Limit - Optional</label>
              <input
                type="number"
                value={form.usageLimit || ''}
                onChange={(e) => setForm({ ...form, usageLimit: e.target.value ? parseInt(e.target.value) : undefined })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Unlimited"
                min="0"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Valid From</label>
              <input
                type="date"
                value={form.validFrom ? form.validFrom.split('T')[0] : ''}
                onChange={(e) => setForm({ ...form, validFrom: new Date(e.target.value).toISOString() })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Valid Until</label>
              <input
                type="date"
                value={form.validUntil ? form.validUntil.split('T')[0] : ''}
                onChange={(e) => setForm({ ...form, validUntil: new Date(e.target.value).toISOString() })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-700 mb-2">Description</label>
              <textarea
                value={form.description || ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={2}
                placeholder="e.g., Get 10% off on orders above ₹300"
              />
            </div>
            <div className="md:col-span-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.isActive !== false}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  aria-label="Active"
                />
                <span className="text-gray-700">Active</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-6">
            <button
              onClick={isAdding ? handleAdd : handleUpdate}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {isAdding ? 'Create Coupon' : 'Update Coupon'}
            </button>
            <button
              onClick={closeFormModal}
              className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {successMessage}
        </div>
      )}

      {isLoading && (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <p className="text-gray-600">Loading coupons...</p>
        </div>
      )}

      {/* Coupons Grid */}
      {!isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {coupons.map(coupon => (
          <div key={coupon.id} className={`bg-white rounded-xl shadow-sm border-2 p-6 ${coupon.isActive ? 'border-blue-200' : 'border-gray-200 opacity-60'}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="w-5 h-5 text-blue-600" />
                  <h3 className="text-gray-900">{coupon.code}</h3>
                </div>
                <p className="text-gray-600 text-sm mb-3">{coupon.description}</p>
              </div>
              <button
                onClick={() => toggleStatus(coupon.id)}
                className={`px-3 py-1 rounded-full text-xs ${
                  coupon.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {coupon.isActive ? 'Active' : 'Inactive'}
              </button>
            </div>

            <div className="space-y-2 mb-4 pb-4 border-b border-gray-200">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Discount:</span>
                <span className="text-gray-900">
                  {coupon.discountType === 'percentage' 
                    ? `${coupon.discountValue}%` 
                    : `₹${coupon.discountValue}`}
                  {coupon.maxDiscount && coupon.discountType === 'percentage' && 
                    ` (max ₹${coupon.maxDiscount})`}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Min. Order:</span>
                <span className="text-gray-900">₹{coupon.minOrderValue}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Valid Until:</span>
                <span className="text-gray-900">
                  {new Date(coupon.validUntil).toLocaleDateString()}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">
                Used: {coupon.usedCount}
                {coupon.usageLimit && ` / ${coupon.usageLimit}`}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setIsAdding(false);
                  setEditingId(coupon.id);
                  setForm(coupon);
                }}
                className="flex-1 p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <Edit className="w-4 h-4 mx-auto" />
              </button>
              <button
                onClick={() => handleDelete(coupon.id)}
                className="flex-1 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4 mx-auto" />
              </button>
            </div>
          </div>
        ))}
        </div>
      )}

      {!isLoading && coupons.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">No coupons yet. Create your first coupon!</p>
        </div>
      )}
    </div>
  );
}
